import type { LedLayout, LedSpec, PanelRef, Vec2, WallMetrics } from '../types';
import { clamp01, mod, smoothstep } from '../math/scalar';
import { spanToMillimetres } from '../params';
import { distanceToOutline } from '../heightfield';
import { offsetConvexPolygon } from '../shapes/tessellate';

/**
 * Where the LED strip runs inside the light boxes.
 *
 * Routing is evaluated in wall coordinates, for the same reason patterns are: a
 * strip has to leave one box and enter the next, and per-panel routing would
 * line up at the joints only by accident.
 *
 * What the routing decides is narrower than it used to be. The strip no longer
 * shapes the panel at all — it lies on a flat frame floor. The routing places
 * the slots the strip passes through, measures how much strip the wall needs,
 * and tells the preview where the light comes from.
 */

export interface StripRouting {
  active: boolean;
  /** `1` on a run, falling to `0` across its width. */
  coverage(worldX: number, worldY: number, localX: number, localY: number): number;
  /** Integral of the coverage profile across a run, in millimetres. */
  crossSection: number;
  /** Distance between runs in millimetres, for the uniformity check. */
  runSpacingMm: number;
}

export interface RoutingOptions {
  spec: LedSpec;
  wall: WallMetrics;
  /** Interior footprint of the light box, in panel space. */
  interior: readonly Vec2[];
  /**
   * Width of the falloff either side of a run, in millimetres.
   *
   * For a light box this only has to give slot edges and the preview a soft
   * boundary. For a channel cut into a panel it is the wall of the groove, and
   * a height field cannot make that wall steeper than one mesh sample — so the
   * caller widens it to match the sampling, and the ramp sits outside the
   * nominal width so the floor still comes out as wide as the strip.
   */
  ramp?: number;
}

const INACTIVE: StripRouting = {
  active: false,
  coverage: () => 0,
  crossSection: 0,
  runSpacingMm: 0,
};

export function createStripRouting(options: RoutingOptions): StripRouting {
  const { spec, wall, interior } = options;
  if (!spec.enabled || spec.stripWidth <= 0) return INACTIVE;

  const halfWidth = spec.stripWidth / 2;
  const ramp = options.ramp ?? 1.2;

  const spacingX = spanToMillimetres(spec.spacing, wall.pitchX);
  const spacingY = spanToMillimetres(spec.spacing, wall.pitchY);

  const runMask = (distance: number): number =>
    1 - smoothstep(halfWidth, halfWidth + ramp, Math.abs(distance));

  const axisDistance = (value: number, spacing: number): number => {
    const phase = mod(value / spacing - spec.offset, 1);
    return Math.min(phase, 1 - phase) * spacing;
  };

  const coverage = (worldX: number, worldY: number, localX: number, localY: number): number => {
    switch (spec.layout as LedLayout) {
      case 'vertical':
        return runMask(axisDistance(worldX, spacingX));
      case 'grid':
        return Math.max(
          runMask(axisDistance(worldX, spacingX)),
          runMask(axisDistance(worldY, spacingY)),
        );
      case 'perimeter':
        // A loop just inside the box wall, so it is panel-local by nature.
        return runMask(distanceToOutline(localX, localY, interior) - spec.inset);
      case 'horizontal':
      default:
        return runMask(axisDistance(worldY, spacingY));
    }
  };

  // What "run spacing" means depends on the layout. For parallel runs it is
  // the distance between them; for a loop it is the width of the unlit middle
  // the loop has to reach across, which is what actually decides whether the
  // centre of the panel comes out darker than its edges.
  const runSpacingMm =
    spec.layout === 'perimeter'
      ? Math.max(0, distanceToOutline(0, 0, interior) - spec.inset) * 2
      : Math.min(spacingX, spacingY);

  return {
    active: true,
    coverage: (wx, wy, lx, ly) => clamp01(coverage(wx, wy, lx, ly)),
    crossSection: spec.stripWidth + ramp,
    runSpacingMm,
  };
}

/**
 * Rim height for one point on a frame's outline.
 *
 * Dropping the rim to the floor where a run crosses the wall opens the slot the
 * strip passes through. When pass-through is off the rim stays full height and
 * the strip is contained in its own box.
 */
export function createRimHeightSampler(
  routing: StripRouting,
  spec: LedSpec,
  panel: PanelRef,
  floorThickness: number,
  fullHeight: number,
): (point: Vec2) => number {
  if (!routing.active || !spec.passThrough) return () => fullHeight;

  const angle = (panel.rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return (point: Vec2): number => {
    const worldX = panel.centerX + point.x * cos - point.y * sin;
    const worldY = panel.centerY + point.x * sin + point.y * cos;
    // A slot wide enough for the strip plus clearance; anything narrower and
    // the strip has to be forced through a printed edge.
    return routing.coverage(worldX, worldY, point.x, point.y) > 0.35 ? floorThickness : fullHeight;
  };
}

/**
 * How much strip the wall needs, measured from the routing.
 *
 * Integrating the coverage and dividing by its cross-section gives the
 * centreline length, which works for every layout — including the perimeter
 * loop — and stays correct if a routing mode is added.
 *
 * The sample count is a budget for the whole wall rather than a fixed grid per
 * panel. The previous version used 128 squared per panel regardless, which on a
 * twelve-by-twelve wall was 2.4 million point-in-polygon tests on every slider
 * drag and made LED mode four times slower than the rest of the pipeline.
 */
const TOTAL_SAMPLE_BUDGET = 200_000;

export interface StripMeasurement {
  /** Strip length for the whole wall, in millimetres. */
  totalMm: number;
  /** Average strip length per panel, in millimetres. */
  perPanelMm: number;
}

export function measureStrip(
  routing: StripRouting,
  panels: readonly PanelRef[],
  interior: readonly Vec2[],
  footprint: { minX: number; minY: number; maxX: number; maxY: number },
): StripMeasurement {
  if (!routing.active || panels.length === 0 || routing.crossSection <= 0) {
    return { totalMm: 0, perPanelMm: 0 };
  }

  const samples = Math.min(
    96,
    Math.max(24, Math.floor(Math.sqrt(TOTAL_SAMPLE_BUDGET / panels.length))),
  );

  const width = footprint.maxX - footprint.minX;
  const height = footprint.maxY - footprint.minY;
  const cellArea = (width / samples) * (height / samples);

  let total = 0;
  for (const panel of panels) {
    const angle = (panel.rotation * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    let area = 0;

    for (let j = 0; j < samples; j += 1) {
      const ly = footprint.minY + ((j + 0.5) * height) / samples;
      for (let i = 0; i < samples; i += 1) {
        const lx = footprint.minX + ((i + 0.5) * width) / samples;
        // Only the box interior can hold strip.
        if (distanceToOutline(lx, ly, interior) <= 0) continue;

        area +=
          routing.coverage(
            panel.centerX + lx * cos - ly * sin,
            panel.centerY + lx * sin + ly * cos,
            lx,
            ly,
          ) * cellArea;
      }
    }
    total += area / routing.crossSection;
  }

  return { totalMm: total, perPanelMm: total / panels.length };
}

/** One continuous piece of strip, in wall coordinates and millimetres. */
export interface StripPath {
  points: Vec2[];
  /** True for the outline loop, which returns to where it started. */
  closed: boolean;
  /** Index of the box this piece lies in, so the preview can place it. */
  panel: number;
}

/**
 * Where the strip physically lies, as paths rather than as a field.
 *
 * `coverage` answers "is there strip at this point", which is what the slots
 * and the measurement need and what the preview cannot draw. These are the
 * same runs read the other way round, reconstructed from the parameters that
 * produced the field rather than traced out of it, so the two are independent
 * and `measureStrip` can be checked against their total length.
 *
 * Everything is clipped to each box's interior, because that is the only place
 * strip can be. Pass-through does not change the path: it decides whether the
 * pieces are one strip or several, not where they lie.
 */
export function traceStrip(
  spec: LedSpec,
  wall: WallMetrics,
  panels: readonly PanelRef[],
  interior: readonly Vec2[],
): StripPath[] {
  if (!spec.enabled || spec.stripWidth <= 0 || interior.length < 3) return [];

  const paths: StripPath[] = [];
  const spacingX = spanToMillimetres(spec.spacing, wall.pitchX);
  const spacingY = spanToMillimetres(spec.spacing, wall.pitchY);

  panels.forEach((panel, index) => {
    const angle = (panel.rotation * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const toWorld = (point: Vec2): Vec2 => ({
      x: panel.centerX + point.x * cos - point.y * sin,
      y: panel.centerY + point.x * sin + point.y * cos,
    });

    if (spec.layout === 'perimeter') {
      const loop = offsetConvexPolygon(interior, spec.inset);
      if (loop) paths.push({ points: loop.map(toWorld), closed: true, panel: index });
      return;
    }

    // A run of constant world Y is, in the panel's own frame, the line whose
    // unit normal is (sin, cos) — and the mirror of that for constant world X.
    if (spec.layout === 'horizontal' || spec.layout === 'grid') {
      collectRuns(paths, index, interior, toWorld, spacingY, spec.offset, panel.centerY, {
        nx: sin,
        ny: cos,
      });
    }
    if (spec.layout === 'vertical' || spec.layout === 'grid') {
      collectRuns(paths, index, interior, toWorld, spacingX, spec.offset, panel.centerX, {
        nx: cos,
        ny: -sin,
      });
    }
  });

  return paths;
}

/** Adds every run of one axis that crosses this box. */
function collectRuns(
  paths: StripPath[],
  panel: number,
  interior: readonly Vec2[],
  toWorld: (point: Vec2) => Vec2,
  spacing: number,
  offset: number,
  centre: number,
  normal: { nx: number; ny: number },
): void {
  if (spacing <= 0) return;

  // How far along the normal this box reaches, in the world axis the runs are
  // spaced on.
  let min = Infinity;
  let max = -Infinity;
  for (const point of interior) {
    const value = centre + point.x * normal.nx + point.y * normal.ny;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  const first = Math.ceil(min / spacing - offset);
  const last = Math.floor(max / spacing - offset);
  // A box smaller than the spacing can still be crossed, but never by enough
  // runs to be worth guarding against; the loop simply does nothing.
  for (let k = first; k <= last; k += 1) {
    const distance = spacing * (k + offset) - centre;
    const origin = { x: normal.nx * distance, y: normal.ny * distance };
    const direction = { x: normal.ny, y: -normal.nx };
    const span = clipRayToConvex(origin, direction, interior);
    if (!span) continue;

    paths.push({
      points: [
        toWorld({ x: origin.x + direction.x * span.from, y: origin.y + direction.y * span.from }),
        toWorld({ x: origin.x + direction.x * span.to, y: origin.y + direction.y * span.to }),
      ],
      closed: false,
      panel,
    });
  }
}

/**
 * The stretch of an infinite line that falls inside a convex polygon.
 *
 * Cyrus–Beck: every edge is a half-plane, and the line's parameter range is
 * whatever survives all of them. Convexity is what makes the result a single
 * interval, and the box interior is convex because it is an offset of a convex
 * outline.
 */
function clipRayToConvex(
  origin: Vec2,
  direction: Vec2,
  polygon: readonly Vec2[],
): { from: number; to: number } | null {
  // Winding decides which side of an edge is inside, so it has to be measured
  // rather than assumed.
  let twiceArea = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    twiceArea += a.x * b.y - b.x * a.y;
  }
  const sign = twiceArea >= 0 ? 1 : -1;

  let from = -Infinity;
  let to = Infinity;

  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    // Inward normal of the edge, for this winding.
    const nx = sign * -(b.y - a.y);
    const ny = sign * (b.x - a.x);

    const denominator = direction.x * nx + direction.y * ny;
    const numerator = (a.x - origin.x) * nx + (a.y - origin.y) * ny;

    if (Math.abs(denominator) < 1e-12) {
      // Parallel to this edge: either wholly inside it or wholly outside.
      if (numerator > 0) return null;
      continue;
    }

    const t = numerator / denominator;
    if (denominator > 0) from = Math.max(from, t);
    else to = Math.min(to, t);
  }

  return to - from > 1e-6 ? { from, to } : null;
}
